import axios, { type AxiosError } from 'axios'

const REDACTED = '[redacted]'
const UNSERIALIZABLE = '[unserializable log data]'

const SECRET_KEY_PATTERN =
  /^(sessionToken|_session_token|_hashed_password|password|cookie|authorization|authData|_auth_data_.+|_perishable_token|_email_verify_token|applePayToken|idToken|accessToken|refreshToken|clientSecret|client_secret)$/i

// Stripe appends payment_intent_client_secret to redirect URLs, and Stripe.js sends client_secret as a query param
const CLIENT_SECRET_QUERY_PARAM_PATTERN = /(client_secret=)[^&#\s"']+/gi

// A failed upload logs its presigned PUT URL, which can overwrite the photo until it expires. S3 echoes the signature
// back in a SignatureDoesNotMatch error, which a wrong Content-Type alone triggers on a valid signature.
const PRESIGNED_URL_SIGNATURE_PATTERN = /(X-Amz-Signature=)[^&\s"]+/g
const S3_ERROR_SIGNATURE_PATTERN = /(<SignatureProvided>)[^<]*/g

export function redactSecretsInText(text: string) {
  return text
    .replace(CLIENT_SECRET_QUERY_PARAM_PATTERN, `$1${REDACTED}`)
    .replace(PRESIGNED_URL_SIGNATURE_PATTERN, `$1${REDACTED}`)
    .replace(S3_ERROR_SIGNATURE_PATTERN, `$1${REDACTED}`)
}

// Axios errors carry the request config, whose headers hold the session token and Cognito JWT
function getAxiosErrorLogObject(error: AxiosError) {
  return {
    name: error.name,
    message: error.message,
    code: error.code,
    method: error.config?.method,
    url: error.config?.url,
    status: error.response?.status,
    responseData: error.response?.data,
  }
}

function redactSecrets(
  this: Record<string, unknown>,
  key: string,
  value: unknown
) {
  if (SECRET_KEY_PATTERN.test(key)) {
    return REDACTED
  }
  const rawValue = this[key]
  if (axios.isAxiosError(rawValue)) {
    return getAxiosErrorLogObject(rawValue)
  }
  if (rawValue instanceof Error) {
    return {
      name: rawValue.name,
      message: rawValue.message,
      stack: rawValue.stack,
    }
  }
  if (typeof value === 'string') {
    return redactSecretsInText(value)
  }
  return value
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    Object.getPrototypeOf(value) === Object.prototype
  )
}

function redactSecretsInEventValue(value: unknown): unknown {
  if (typeof value === 'string') {
    return redactSecretsInText(value)
  }
  if (Array.isArray(value)) {
    return value.map(redactSecretsInEventValue)
  }
  if (isPlainObject(value)) {
    return redactSecretsInEventProperties(value)
  }
  return value
}

// Unlike redactSecretsForLog, skips the JSON round trip so SDKs still receive the original value types
export function redactSecretsInEventProperties(
  eventProperties: Record<string, unknown>
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(eventProperties).map(([key, value]) => [
      key,
      SECRET_KEY_PATTERN.test(key)
        ? REDACTED
        : redactSecretsInEventValue(value),
    ])
  )
}

export function redactSecretsForLog(
  logData: Record<string, unknown>
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(logData).map(([key, value]) => {
      if (SECRET_KEY_PATTERN.test(key)) {
        return [key, REDACTED]
      }
      if (value === undefined) {
        return [key, value]
      }
      try {
        return [key, JSON.parse(JSON.stringify(value, redactSecrets))]
      } catch {
        return [key, UNSERIALIZABLE]
      }
    })
  )
}
